import ModalViewModel from 'infra/ModalViewModel'
import Document from 'documents/Document'
import { action, computed, intercept, observable, makeObservable } from 'mobx'
import TabController from 'infra/TabController'
import { ConfirmViewModel } from 'components/ConfirmDialog/ConfirmViewModel'
import DocumentTag from 'documents/DocumentTag'
import { required, validationContext } from 'validx'
import { Id, Nullable } from 'types/definitions'
import { task } from 'mobx-task'
import { AlertState } from 'alerts/Alert'
import DocumentAccess from 'documents/DocumentAccess'
import DocumentVersion from 'documents/DocumentVersion'
import orderBy from 'lodash/orderBy'
import { DocumentDetailsModalPorts } from './ports'
import { DocumentJobAccessEditPermission } from 'documents/dtos/document-access'
import DocumentType from 'documents/DocumentType'
import isDocumentVersionsTabDisabled from 'documents/DocumentVersionsFeatureToggle'

export class DocumentDetailsModalViewModel extends ModalViewModel {
  @observable title = ''
  @observable description = ''
  @observable tags: Array<DocumentTag> = []
  @observable documentType: Nullable<DocumentType> = null

  descriptionLengthLimit = 200

  validation = validationContext(this, Document.validationRules)

  confirmAccessDelete = new ConfirmViewModel()
  confirmDocumentDelete = new ConfirmViewModel()

  @observable uploadVersionModalVM: NewVersionModalViewModel | null = null

  @observable
  revertVersionModalVM: RevertVersionModalViewModel | null = null

  @observable versionSortOrder: 'asc' | 'desc' = 'desc'

  @observable viewAsAdmin = false

  tabController = new TabController<Tabs>(() => [
    {
      tab: Tabs.Details,
      title: 'Details',
      hasError: () => false,
    },
    {
      tab: Tabs.Sharing,
      title: 'Sharing',
      hasError: () => false,
    },
    ...(this.isVersionsTabDisabled
      ? []
      : [
          {
            tab: Tabs.Versions,
            title: 'Versions',
            hasError: () => false,
          },
        ]),
  ])

  constructor(
    public document: Document,
    private adapter: DocumentDetailsModalPorts,
    private _viewAsAdmin?: boolean,
  ) {
    super()
    makeObservable(this)
    this.title = this.document.title
    this.description = this.document.description
    this.tags = this.document.tags
    this.documentType = this.document.documentType
    this.viewAsAdmin = this._viewAsAdmin ?? false
  }

  @computed
  get isDetailsTab() {
    return this.tabController.current.tab === Tabs.Details
  }

  @computed
  get documentVersions() {
    return this.document.versions
  }

  @computed
  get hasChanges() {
    return (
      this.document.title !== this.title ||
      this.document.description !== this.description ||
      !this.tags.every((draftTag) => this.document.tags.includes(draftTag)) ||
      this.document.documentType !== this.documentType
    )
  }

  @computed
  get sortedDocumentVersions() {
    return orderBy(
      this.documentVersions,
      ['dateCreated'],
      this.versionSortOrder,
    )
  }

  @computed
  get documentTypes() {
    return [...this.adapter.documentTypes.items]
  }

  @computed
  get canChangedocumentType() {
    return !this.document.type?.id || this.viewAsAdmin
  }

  @computed
  get canUploadVersion() {
    return (
      this.canEdit &&
      this.adapter.getCurrentMember().hasSomePermissions('DOCS_UPLOAD')
    )
  }

  @computed
  get isVersionsTabDisabled() {
    return isDocumentVersionsTabDisabled()
  }

  @computed
  get canEdit() {
    return this.document.canEdit
  }

  @computed
  get headerDescription() {
    if (this.description !== '') {
      if (this.document.description !== this.description) {
        return this.description
      }
      return this.document.description
    }

    return ''
  }

  @computed
  get descriptionCharsRemaining() {
    if (this.description) {
      return this.descriptionLengthLimit - this.description?.length
    }
    return this.descriptionLengthLimit
  }

  @computed
  get headerTitle() {
    if (this.title !== '') {
      if (this.document.title !== this.title) {
        return this.title
      }

      return this.document.title
    }

    return ''
  }

  @computed
  get backingFileName() {
    return this.document.latestVersion?.files[0].fileName
  }

  @computed
  get documentWorkspace() {
    return this.document.workspace
  }

  @computed
  get canDownload() {
    return this.document.downloadable
  }

  @computed
  get canRemove() {
    return this.adapter
      .getCurrentMember()
      .hasSomePermissions(
        'DOCS_SELF_REMOVE',
        'DOCS_CLIENT_REMOVE',
        'DOCS_PROVIDER_REMOVE',
      )
  }

  activate = task.resolved(async () => {
    await this.fetchTypes()
    await this.fetchAccessInfo()
  })

  deactivate() {
    this.close()
  }

  close() {
    this.confirmAccessDelete.decline()
    this.confirmDocumentDelete.decline()
    this.uploadVersionModalVM?.cancel()
    this.discardChanges()
    super.close()
  }

  @action
  discardChanges() {
    this.title = this.document.title
    this.description = this.document.description
    this.documentType = this.document.documentType
    this.validation.reset()
  }

  @action.bound
  setVersionsSort(sortOrder: 'asc' | 'desc') {
    this.versionSortOrder = sortOrder
  }

  @action.bound
  uploadVersion(file: File) {
    this.uploadVersionModalVM = this.createNewVersionModalViewModel(file)
    this.uploadVersionModalVM.show()
    // In case the caller wants to know when the process is complete/aborted
    return this.uploadVersionModalVM.close$
  }

  @action.bound
  revertVersion(targetVersion: DocumentVersion) {
    this.revertVersionModalVM =
      this.createRevertVersionModalViewModel(targetVersion)
    this.revertVersionModalVM.show()
  }

  @action.bound
  setViewAsAdmin(viewAsAdmin: boolean) {
    this.viewAsAdmin = viewAsAdmin
  }

  @action.bound
  setDocumentType(type: Nullable<DocumentType>) {
    if (this.canChangedocumentType) {
      this.documentType = type
    }
  }

  @action.bound
  downloadLatest() {
    if (this.canDownload) {
      this.adapter.downloadDocument(this.document)
    }
  }

  @action.bound
  downloadVersion(version: DocumentVersion) {
    this.adapter.downloadDocumentVersion(this.document, version)
  }

  fetchTypes = async () => {
    await this.adapter.fetchTypes()
  }

  save = task.resolved(async () => {
    const valid = this.validation.reset().validate().isValid
    if (!valid) {
      return
    }
    try {
      if (
        this.documentType?.shortId &&
        this.canChangedocumentType &&
        this.document.documentType !== this.documentType
      ) {
        await this.adapter.assignType(this.document, {
          type: this.documentType.shortId,
        })
      }

      await this.adapter
        .updateDocument(this.document, {
          title: this.title,
          description: this.description,
          tags: this.tags.map((tag) => Id.assert(tag.id)),
        })
        .then(() =>
          this.adapter
            .newAlert()
            .withState(AlertState.Success)
            .saying(
              `Success! Document ${this.document.title} has been updated!`,
            )
            .autoDismiss()
            .show(),
        )
      this.close()
    } catch (error) {
      console.error(error)
    }
  })

  delete = task.resolved(async () => {
    if (!(await this.confirmDocumentDelete.show())) {
      return
    }

    try {
      const docTitle = this.document.title
      await this.adapter.archiveDocument(this.document)

      this.adapter
        .newAlert()
        .withState(AlertState.Success)
        .saying(`Success!  Document ${docTitle} has been removed!`)
        .autoDismiss()
        .show()

      this.close()
    } catch (error) {
      this.adapter.newAlert().fromError(error).show()
      console.error(error)
    }
  })

  fetchAccessInfo = task(
    async () => await this.adapter.fetchAccessInfo(this.document),
  )

  changeAccessPermission = task.resolved(
    async (
      access: DocumentAccess,
      newPermission: DocumentJobAccessEditPermission,
    ) => {
      await this.adapter.updateAccess(Id.assert(access.id), {
        edit_permission: newPermission,
      })
    },
  )

  deleteAccess = task.resolved(async (access: DocumentAccess) => {
    if (!(await this.confirmAccessDelete.show())) {
      return
    }

    await this.adapter.deleteAccess(Id.assert(access.id))
  })

  private createNewVersionModalViewModel(file: File) {
    return new NewVersionModalViewModel(this.document, this.adapter, file)
  }

  private createRevertVersionModalViewModel(targetVersion: DocumentVersion) {
    return new RevertVersionModalViewModel(
      this.document,
      this.adapter,
      targetVersion,
    )
  }
}

export const enum Tabs {
  Details,
  Sharing,
  Versions,
}

export class NewVersionModalViewModel extends ModalViewModel {
  @observable message = ''

  validation = validationContext(this, {
    message: [required('Message is required.')],
  })

  constructor(
    public document: Document,
    private adapter: DocumentDetailsModalPorts,
    public file: File,
  ) {
    super()
    makeObservable(this)
    // Clear validation on field changes
    intercept(this, (change) => {
      this.validation.clearErrors(String(change.name))
      return change
    })

    this.message = `Version ${this.versionsCount + 1}`
  }

  @computed
  get versionsCount() {
    return this.document.versions.length
  }

  @action.bound
  cancel() {
    this.close()
  }

  uploadVersion = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    try {
      await this.adapter.createVersion(this.document, this.file, this.message)
      this.adapter
        .newAlert()
        .withState(AlertState.Success)
        .saying(
          `Success!`,
          `A new version of ${this.document.title} has been uploaded!`,
        )
        .autoDismiss()
        .show()

      this.close()
    } catch (error) {
      this.adapter.newAlert().fromError(error).show()
      console.error(error)
      this.close()
    }
  })
}

export class RevertVersionModalViewModel extends ModalViewModel {
  @observable message = `Revert to version: ${this.targetVersion.message}`

  validation = validationContext(this, {
    message: [required('Message is required.')],
  })

  constructor(
    public document: Document,
    private adapter: DocumentDetailsModalPorts,
    public targetVersion: DocumentVersion,
  ) {
    super()
    makeObservable(this)
    // Clear validation on field changes
    intercept(this, (change) => {
      this.validation.clearErrors(String(change.name))
      return change
    })
  }

  @computed
  get targetVersionFile() {
    return this.targetVersion.files[0]
  }

  @action.bound
  cancel() {
    this.close()
  }

  revertVersion = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    try {
      await this.adapter
        .revertToVersion(this.document, this.targetVersion, this.message)
        .then(() => {
          this.adapter
            .newAlert()
            .withState(AlertState.Success)
            .saying(
              `Success! Document ${this.document.title} has been reverted to ${this.targetVersion.message}!`,
            )
            .autoDismiss()
            .show()

          this.close()
        })
    } catch (error) {
      this.adapter.newAlert().fromError(error).show()
      console.error(error)
      this.close()
    }
  })
}
